import { useQueries, useQuery } from "@tanstack/react-query";
import { produce } from "immer";

import {
  GraphQLAPI,
  GraphQLResult,
  gql,
  queries,
  typedGql,
} from "@examspark/api";
import { collectAllPages, parseAwsJson } from "@examspark/common";
import { parseTCMPCUnit } from "@examspark/customer/tcmpc";
import {
  Course,
  GenericReferenceId,
  ParsedSearchableResourceConnectionNew,
  ParsedUnit,
  SearchableResourceConnectionNew,
  Unit,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

export const useQuerySearchUnitExams = (unitId?: string) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    enabled: !!unitId,
    queryKey: ["unit", unitId, "exams"],
    queryFn: async () =>
      gql.operations.unit.searchUnitExams(unitId, authorizationContext),
  });
};

export const useQuerySearchUnitDocuments = (unit?: Unit | null) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["unit", unit?.id, "documents"],
    enabled: !!unit,
    queryFn: async () => {
      const allResults = await collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query<{
          search: SearchableResourceConnectionNew;
        }>({
          query: queries.search.search({
            label: "SearchUnitDocuments",
            fields: `
            id
            sk
            workspaceId
            name
            type
            file {
              bucket
              key
              mimetype
              region
            }
            resource {
              id
              sk
              name
            }
          `,
          }),
          variables: {
            authorizationContext,
            nextToken,
            filter: {
              sk: {
                eq: "resource#v0",
              },
              or: [
                {
                  referencesIds: {
                    eq: unit?.id ?? "__empty__",
                  },
                },
                {
                  referencesIds: {
                    eq: unit?.course?.id ?? "__empty__",
                  },
                },
              ],
            },
            sort: [
              {
                field: "name",
                direction: "asc",
              },
            ],
          },
        })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

        const next = produce(result.data?.search, (draft) => {
          if (!draft) return;
          if (typeof draft.aggregations === "string") {
            draft.aggregations = JSON.parse(draft.aggregations);
          }
        }) as ParsedSearchableResourceConnectionNew;

        return next;
      });

      return allResults.flatMap((page) => page.items ?? []);
    },
  });
};

export const useQueryGetUnitV2 = (unitId: string | undefined) => {
  return useQuery({
    queryKey: ["unit", unitId],
    enabled: unitId != null,
    keepPreviousData: true,
    useErrorBoundary: true,
    queryFn: async () => {
      // re-check guard to make ts happy
      if (!unitId) return null;

      const result = (
        await typedGql(queries.unit.getUnit)({
          input: {
            id: unitId,
            sk: "unit#v0",
          },
        })
      ).data?.getUnit;

      if (!result) return null;

      const next = produce(result, (draft) => {
        if (!draft) return;
        draft.data = parseAwsJson(draft.data);

        draft.subUnits = draft.subUnits?.map((sub) => {
          if (!sub) return sub;
          sub.data = parseAwsJson(sub.data);
          return sub;
        });
      });

      return next as ParsedUnit;
    },
  });
};

// really useQueryGetTCMPCUnit
export const _useQueryGetUnit = (
  unitId: string | undefined,
  unitSk: `unit#v${number}` = "unit#v0",
  /** using this for better navigation experience between units */
  keepPreviousData = false,
) => {
  return {
    queryKey: ["unit", unitId],
    enabled: unitId != null,
    keepPreviousData,
    useErrorBoundary: true,
    queryFn: async () => {
      // re-check guard to make ts happy
      if (!unitId) return null;

      const result = await typedGql(queries.unit.getUnit)({
        input: {
          id: unitId,
          sk: unitSk,
        },
      });

      if (!result.data) return null;

      return parseTCMPCUnit(result.data.getUnit);
    },
  };
};

export const useQueryGetUnit = (
  unitId: string | undefined,
  unitSk: `unit#v${number}` = "unit#v0",
  /** using this for better navigation experience between units */
  keepPreviousData = false,
) => {
  return useQuery(_useQueryGetUnit(unitId, unitSk, keepPreviousData));
};

export const useQueriesGetUnits = (
  unitRefs: GenericReferenceId[],
  keepPreviousData = false,
) => {
  return useQueries({
    queries: unitRefs.map(({ id }) =>
      _useQueryGetUnit(id, "unit#v0", keepPreviousData),
    ),
  });
};

export const useQueryGetUnitLayoutsForCourse = (course: Course | undefined) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["course", course?.id, "layouts"],
    enabled: !!course?.id,
    queryFn: async () => {
      if (!course?.id) return null;

      const result = await typedGql(queries.course.listUnitLayouts)({
        input: {
          id: course.id,
          workspaceId: course.workspaceId,
        },
        authorizationContext,
      });

      return result.data?.listUnitLayouts.items ?? [];
    },
  });
};
